import React, { useEffect, useRef, useState } from 'react';
import api from '../../utils/api';
import { C, btn } from './LeadForms';

/**
 * Lead Management — shared tools: CSV download, the Export ▾ menu (Leads list + Analytics), and a CSV /
 * Excel reader for the HO imports (pincode map, LSQ leads). Server: /api/leads/reports, /api/leads/admin.
 */

// GET a CSV from the API and save it.
export async function downloadCsv(path, params, filename) {
  const res = await api.get(path, { params, responseType: 'blob' });
  const cd = res.headers?.['content-disposition'] || '';
  const name = (cd.match(/filename="([^"]+)"/) || [])[1] || filename || 'export.csv';
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

const thisMonth = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 7);

// Orange Export ▾ button. `params` = current store + filters; `period` = { from, to } for tasks / activity.
export function ExportMenu({ params, period, onError }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [month, setMonth] = useState(thisMonth());
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const run = async (kind, extra = {}) => {
    setBusy(kind);
    try { await downloadCsv(`/leads/reports/export/${kind}`, { ...params, ...extra }); setOpen(false); } catch (e) {
      let msg = 'Export failed';
      try { const t = await e?.response?.data?.text?.(); if (t) msg = JSON.parse(t).error || msg; } catch { /* error body was not JSON */ }
      if (onError) onError(msg);
    }
    setBusy('');
  };
  const item = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: '#fff', fontSize: 13, color: C.text, cursor: 'pointer', borderRadius: 6 };
  const p = period || {};
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{ ...btn('primary'), background: '#ea580c' }}>Export ▾</button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: 6, width: 290, boxShadow: '0 10px 28px rgba(0,0,0,0.12)' }}>
          <button style={item} disabled={!!busy} onClick={() => run('leads')}>{busy === 'leads' ? 'Preparing…' : 'Leads list (CSV) — current filters'}</button>
          <button style={item} disabled={!!busy} onClick={() => run('tasks', p)}>{busy === 'tasks' ? 'Preparing…' : `Tasks (CSV)${p.from ? ` — ${p.from} → ${p.to}` : ' — this month'}`}</button>
          <button style={item} disabled={!!busy} onClick={() => run('activities', p)}>{busy === 'activities' ? 'Preparing…' : `Activity history (CSV)${p.from ? ` — ${p.from} → ${p.to}` : ' — this month'}`}</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px' }}>
            <button style={{ ...item, padding: 0, width: 'auto', flex: 1 }} disabled={!!busy} onClick={() => run('incentive', { month })}>{busy === 'incentive' ? 'Preparing…' : 'Incentive extract (CSV)'}</button>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: '3px 6px', fontSize: 12 }} />
          </div>
          <button style={item} disabled={!!busy} onClick={() => run('daily', p)}>{busy === 'daily' ? 'Preparing…' : 'Daily counts by store × source (CSV)'}</button>
          <div style={{ fontSize: 11, color: C.faint, padding: '6px 12px 2px' }}>Exports follow the store on screen and are logged in the Activity Log. HO schedules the weekly email in Settings.</div>
        </div>
      )}
    </div>
  );
}

// ── file → rows ([{ header: value }]) ─────────────────────────────────────────
function parseCsv(text) {
  const rows = []; let row = [], cell = '', q = false;
  const s = text.replace(/^﻿/, '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && s[i + 1] === '\n') i++; row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter(r => r.some(c => String(c).trim() !== ''));
}
const toObjects = (grid) => {
  const [head, ...body] = grid;
  if (!head) return [];
  const keys = head.map(h => String(h || '').trim());
  return body.map(r => { const o = {}; keys.forEach((k, i) => { if (k) o[k] = r[i] == null ? '' : r[i]; }); return o; });
};
// Excel dates come back as UTC-labelled wall-clock times → send them as plain "YYYY-MM-DD HH:mm:ss" (IST on the server).
const cellValue = (v) => {
  if (v == null) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 19).replace('T', ' ');
  if (typeof v === 'object') {
    if (v.richText) return v.richText.map(t => t.text).join('');
    if (v.text != null) return String(v.text);
    if (v.result != null) return cellValue(v.result);
  }
  return v;
};
export async function readSheetFile(file) {
  if (/\.xlsx$/i.test(file.name)) {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.worksheets[0];
    if (!ws) return [];
    const grid = [];
    ws.eachRow({ includeEmpty: false }, (r) => { const vals = []; for (let c = 1; c <= ws.columnCount; c++) vals.push(cellValue(r.getCell(c).value)); grid.push(vals); });
    return toObjects(grid);
  }
  if (/\.(csv|txt)$/i.test(file.name)) return toObjects(parseCsv(await file.text()));
  throw new Error('Choose a .csv or .xlsx file');
}
export const _test = { parseCsv, toObjects };
